/**
 * Структура компании (правка №9) — чистая логика: дерево, раскладка карточек,
 * счёт людей, поиск, формат данных сотрудника. [Claude] 2026-11-02
 * Доработка 03.11.2026 (Чат 4): горизонтальная раскладка, ручной сдвиг веток, скрытые
 * у пользователя подразделения, список сотрудников на карточке, ЧС/ЧМ, попадание в карточку.
 */

export const CARD_W = 248;
export const H_GAP = 24;
export const V_GAP = 56;
export const LEVEL_GAP_H = 72; // между колонками в горизонтальной раскладке
export const ROW_GAP_H = 16; // между карточками одной колонки в горизонтальной раскладке
export const MAX_HEADS_ON_CARD = 2;
export const MAX_MEMBERS_ON_CARD = 5;

const ROLE_ORDER = { head: 0, deputy: 1, member: 2 };

/** byId, children (parent id → child ids in order), roots. A unit whose parent is missing goes to the top. */
export function buildTree(units) {
    const byId = new Map();
    (units || []).forEach((u) => byId.set(u.id, u));
    const children = new Map();
    const roots = [];
    const sorted = [...byId.values()].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
    sorted.forEach((u) => {
        const parent = u.parent_id != null && byId.has(u.parent_id) && u.parent_id !== u.id ? u.parent_id : null;
        if (parent == null) {
            roots.push(u.id);
        } else {
            if (!children.has(parent)) children.set(parent, []);
            children.get(parent).push(u.id);
        }
    });
    return { byId, children, roots };
}

export function childIds(tree, id) {
    return tree.children.get(id) || [];
}

/** The unit and all its sub-units (safe against broken cyclic data). */
export function subtreeIds(tree, id) {
    const out = [];
    const seen = new Set();
    const stack = [id];
    while (stack.length) {
        const cur = stack.pop();
        if (seen.has(cur) || !tree.byId.has(cur)) continue;
        seen.add(cur);
        out.push(cur);
        childIds(tree, cur).forEach((c) => stack.push(c));
    }
    return out;
}

/** Parents from the root down to (not including) the unit. */
export function ancestorIds(tree, id) {
    const out = [];
    const seen = new Set([id]);
    let cur = tree.byId.get(id)?.parent_id;
    while (cur != null && tree.byId.has(cur) && !seen.has(cur)) {
        seen.add(cur);
        out.unshift(cur);
        cur = tree.byId.get(cur).parent_id;
    }
    return out;
}

/** Can `unitId` be moved under `newParentId` (null = top level)? */
export function canMoveUnit(tree, unitId, newParentId) {
    if (newParentId == null) return true;
    if (newParentId === unitId || !tree.byId.has(newParentId)) return false;
    return !ancestorIds(tree, newParentId).includes(unitId);
}

export function membersByUnit(members) {
    const map = new Map();
    (members || []).forEach((m) => {
        if (!map.has(m.unit_id)) map.set(m.unit_id, []);
        map.get(m.unit_id).push(m);
    });
    map.forEach((list) => list.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9)
        || (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id));
    return map;
}

export function leadersOf(byUnit, unitId) {
    return (byUnit.get(unitId) || []).filter((m) => m.role === "head" || m.role === "deputy");
}

export function staffOf(byUnit, unitId) {
    return (byUnit.get(unitId) || []).filter((m) => m.role === "member");
}

/** Unique people of the unit, optionally with all sub-units. */
export function peopleCount(tree, byUnit, unitId, withSubunits = true) {
    const ids = withSubunits ? subtreeIds(tree, unitId) : [unitId];
    const people = new Set();
    ids.forEach((id) => (byUnit.get(id) || []).forEach((m) => people.add(m.employee_id)));
    return people.size;
}

/**
 * Card height: title + up to two leaders (each with the chosen data lines) + the staff list
 * (up to MAX_MEMBERS_ON_CARD lines and «ещё N») + footer «N отделов».
 * staffCount = -1 hides the staff block (the user turned the list off).
 */
export function cardHeight(leadersCount, fieldLines, staffCount = -1) {
    const shown = Math.min(leadersCount, MAX_HEADS_ON_CARD);
    const person = 36 + 14 * Math.max(0, fieldLines);
    const people = shown ? shown * person : 24;
    const more = leadersCount > MAX_HEADS_ON_CARD ? 16 : 0;
    let staff = 0;
    if (staffCount >= 0) {
        const lines = Math.min(staffCount, MAX_MEMBERS_ON_CARD);
        staff = 22 + (lines ? lines * 18 : 18) + (staffCount > MAX_MEMBERS_ON_CARD ? 16 : 0) + 4;
    }
    return 40 + people + more + staff + 32;
}

/**
 * Tidy tree layout. direction "v" — сверху вниз (родитель над детьми), "h" — слева направо
 * (родитель слева, дети столбиком справа). Свёрнутые прячут ветку, скрытые (hidden) не рисуются вовсе.
 */
export function layoutTree(tree, { collapsed = new Set(), hidden = new Set(), heightOf = () => 120, direction = "v" } = {}) {
    const horizontal = direction === "h";
    const span = new Map(); // ширина ветки поперёк направления роста
    const levelSize = []; // высота уровня (v) или ширина колонки (h)
    const visibleKids = (id) => (collapsed.has(id) ? [] : childIds(tree, id).filter((k) => !hidden.has(k)));
    const roots = tree.roots.filter((r) => !hidden.has(r));
    const own = (id) => (horizontal ? heightOf(id) : CARD_W);
    const gap = horizontal ? ROW_GAP_H : H_GAP;

    const measure = (id, depth, seen) => {
        if (seen.has(id)) return own(id);
        seen.add(id);
        levelSize[depth] = Math.max(levelSize[depth] || 0, horizontal ? CARD_W : heightOf(id));
        const kids = visibleKids(id);
        let s = own(id);
        if (kids.length) {
            const sum = kids.reduce((acc, k) => acc + measure(k, depth + 1, seen), 0) + gap * (kids.length - 1);
            s = Math.max(s, sum);
        }
        span.set(id, s);
        return s;
    };
    const seenMeasure = new Set();
    roots.forEach((r) => measure(r, 0, seenMeasure));

    const levelPos = [];
    const levelGap = horizontal ? LEVEL_GAP_H : V_GAP;
    levelSize.forEach((h, i) => {
        levelPos[i] = i === 0 ? 0 : levelPos[i - 1] + levelSize[i - 1] + levelGap;
    });

    const nodes = [];
    const edges = [];
    const place = (id, start, depth, seen) => {
        if (seen.has(id)) return;
        seen.add(id);
        const s = span.get(id) || own(id);
        const h = heightOf(id);
        const cross = start + (s - own(id)) / 2;
        nodes.push(horizontal
            ? { id, x: levelPos[depth] || 0, y: cross, w: CARD_W, h, depth }
            : { id, x: cross, y: levelPos[depth] || 0, w: CARD_W, h, depth });
        let cursor = start;
        visibleKids(id).forEach((k) => {
            edges.push({ from: id, to: k });
            place(k, cursor, depth + 1, seen);
            cursor += (span.get(k) || own(k)) + gap;
        });
    };
    const seenPlace = new Set();
    let start = 0;
    roots.forEach((r) => {
        place(r, start, 0, seenPlace);
        start += (span.get(r) || own(r)) + gap * 2;
    });
    const crossTotal = Math.max(0, start - gap * 2);
    const last = levelSize.length - 1;
    const mainTotal = last >= 0 ? levelPos[last] + levelSize[last] : 0;
    return {
        nodes,
        edges,
        direction: horizontal ? "h" : "v",
        width: horizontal ? mainTotal : crossTotal,
        height: horizontal ? crossTotal : mainTotal,
    };
}

/**
 * Ручной сдвиг: у каждой карточки — свой сдвиг плюс сдвиги всех её вышестоящих (ветка едет целиком).
 * offsets: Map(unit_id → {dx, dy}). extra: {id, dx, dy} — сдвиг во время перетаскивания.
 * Возвращает новую раскладку с пересчитанными размерами холста (minX/minY — для отрицательных координат).
 */
export function applyOffsets(layout, tree, offsets = new Map(), extra = null) {
    const own = (id) => {
        const o = offsets.get(id) || { dx: 0, dy: 0 };
        if (extra && extra.id === id) return { dx: o.dx + extra.dx, dy: o.dy + extra.dy };
        return o;
    };
    let minX = 0;
    let minY = 0;
    let maxX = layout.width;
    let maxY = layout.height;
    const nodes = layout.nodes.map((n) => {
        let dx = 0;
        let dy = 0;
        [...ancestorIds(tree, n.id), n.id].forEach((id) => {
            const o = own(id);
            dx += o.dx || 0;
            dy += o.dy || 0;
        });
        const moved = dx || dy ? { ...n, x: n.x + dx, y: n.y + dy } : n;
        minX = Math.min(minX, moved.x);
        minY = Math.min(minY, moved.y);
        maxX = Math.max(maxX, moved.x + moved.w);
        maxY = Math.max(maxY, moved.y + moved.h);
        return moved;
    });
    return { ...layout, nodes, minX, minY, width: maxX, height: maxY };
}

/** Какая карточка под точкой холста (x, y); exclude — id, которые не считаются (сама ветка). */
export function hitTest(nodes, x, y, exclude = new Set()) {
    for (let i = nodes.length - 1; i >= 0; i -= 1) {
        const n = nodes[i];
        if (exclude.has(n.id)) continue;
        if (x >= n.x && x <= n.x + n.w && y >= n.y && y <= n.y + n.h) return n.id;
    }
    return null;
}

/** Путь соединительной линии: вертикально — «колено» вниз, горизонтально — «колено» вправо. */
export function edgePath(a, b, direction = "v", gap = V_GAP) {
    if (direction === "h") {
        const y1 = a.y + a.h / 2;
        const y2 = b.y + b.h / 2;
        const mid = b.x - LEVEL_GAP_H / 2;
        return `M${a.x + a.w},${y1} H${mid} V${y2} H${b.x}`;
    }
    const x1 = a.x + a.w / 2;
    const x2 = b.x + b.w / 2;
    const mid = b.y - gap / 2;
    return `M${x1},${a.y + a.h} V${mid} H${x2} V${b.y}`;
}

/** Пунктир дополнительного подчинения — плавная кривая от вышестоящего к подразделению. */
export function linkPath(a, b, direction = "v") {
    if (direction === "h") {
        const x1 = a.x + a.w;
        const y1 = a.y + a.h / 2;
        const x2 = b.x;
        const y2 = b.y + b.h / 2;
        const c = Math.max(40, Math.abs(x2 - x1) / 2);
        return `M${x1},${y1} C${x1 + c},${y1} ${x2 - c},${y2} ${x2},${y2}`;
    }
    const x1 = a.x + a.w / 2;
    const y1 = a.y + a.h;
    const x2 = b.x + b.w / 2;
    const y2 = b.y;
    const c = Math.max(40, Math.abs(y2 - y1) / 2);
    return `M${x1},${y1} C${x1},${y1 + c} ${x2},${y2 - c} ${x2},${y2}`;
}

const norm = (s) => String(s || "").toLowerCase().replace(/ё/g, "е").trim();

/** Units matching a query by unit name or by a person (name, title, position) in it. */
export function searchUnits(query, units, members, employeesById) {
    const q = norm(query);
    if (!q) return [];
    const hits = new Set();
    (units || []).forEach((u) => {
        if (norm(u.name).includes(q)) hits.add(u.id);
    });
    (members || []).forEach((m) => {
        const e = employeesById.get(m.employee_id);
        if (!e) return;
        if (norm(e.full_name).includes(q) || norm(m.title).includes(q) || norm(e.position).includes(q)) hits.add(m.unit_id);
    });
    return (units || []).filter((u) => hits.has(u.id)).map((u) => u.id);
}

export function initials(name) {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "?";
    return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

/** «Иванова Мария Петровна» → «Иванова Мария» — короче для строк списка на карточке. */
export function shortName(name) {
    const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).join(" ") || "—";
}

function ruDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
    return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

export function plural(n, one, few, many) {
    const a = Math.abs(n) % 100;
    const b = a % 10;
    if (a > 10 && a < 20) return many;
    if (b > 1 && b < 5) return few;
    if (b === 1) return one;
    return many;
}

/** «стаж 2 г. 3 мес.» from the hire date to `today`. */
export function tenure(iso, today = new Date()) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
    if (!m) return null;
    let months = (today.getFullYear() - Number(m[1])) * 12 + (today.getMonth() + 1 - Number(m[2]));
    if (today.getDate() < Number(m[3])) months -= 1;
    if (months < 0) return null;
    const y = Math.floor(months / 12);
    const mo = months % 12;
    if (!y && !mo) return "меньше месяца";
    return [y ? `${y} ${plural(y, "год", "года", "лет")}` : "", mo ? `${mo} мес.` : ""].filter(Boolean).join(" ");
}

/** One data line of a person for the chosen field, or null if empty / hidden. */
export function formatField(key, emp, today = new Date()) {
    if (!emp || (emp.hidden || []).includes(key)) return null;
    const v = emp[key];
    switch (key) {
        case "birth_date":
            return ruDate(v) ? `ДР ${ruDate(v)}` : null;
        case "numerology":
            return v ? `ЧС/ЧМ ${v}` : null;
        case "date_hired": {
            const d = ruDate(v);
            if (!d) return null;
            const t = tenure(v, today);
            return t ? `с ${d} · ${t}` : `с ${d}`;
        }
        case "children":
            return v ? `детей: ${v}` : null;
        case "telegram":
            return v ? (String(v).startsWith("@") ? String(v) : `@${v}`) : null;
        default:
            return v ? String(v) : null;
    }
}
